import type { LayerDocument, MapDocument } from "./types.ts";

/**
 * The Players band (ADR-047): where characters draw among a map's layers. `playersAbove` counts the ground
 * layers under the band; a map that has not pinned it uses the lowest prop layer, or every layer when it
 * has none.
 */

/**
 * The default band for a layer list: the index of the lowest prop layer, else the layer count.
 *
 * @param layers Layers in draw order.
 * @returns The number of ground layers.
 */
export function defaultPlayersAbove(layers: readonly LayerDocument[]): number {
  const lowestProp = layers.findIndex((l) => l.kind === "prop");
  return lowestProp < 0 ? layers.length : lowestProp;
}

/**
 * The map's band, pinned or defaulted.
 *
 * @param map The map.
 * @returns The number of ground layers.
 */
export function playersAbove(map: MapDocument): number {
  return map.playersAbove ?? defaultPlayersAbove(map.layers);
}

/**
 * How a layer draws against characters (ADR-047's amendment of 2026-10-03): `ground` under the band, under
 * every character; in the band, `sorts` for prop and tile layers, which sort with characters by feet row;
 * `flat` for terrain layers, drawn whole at the band's floor, still under every character; `unseen` for
 * layers with no art of their own in the band (entity markers, collision, zones).
 */
export type BandRole = "ground" | "sorts" | "flat" | "unseen";

/**
 * The role a layer plays against characters, from its kind and its side of the Players band.
 *
 * @param kind The layer's kind.
 * @param index The layer's index, bottom first.
 * @param band The number of ground layers (`playersAbove`).
 * @returns The role.
 */
export function bandRole(kind: LayerDocument["kind"], index: number, band: number): BandRole {
  if (index < band) {
    return "ground";
  }
  if (kind === "prop" || kind === "tile") {
    return "sorts";
  }
  return kind === "terrain" ? "flat" : "unseen";
}
